/**
 * useFeatureGate — Schlanker Feature-Gate-Hook auf Basis der Pricing-Konfiguration.
 *
 * Prüft, ob ein bestimmtes Feature für den aktuellen Plan freigeschaltet ist,
 * und liefert Upgrade-Infos wenn nicht.
 *
 * Rein clientseitig — kein Backend, kein Async, keine Side Effects.
 * Datenquelle: src/data/pricing.js (featureGates pro Plan).
 *
 * ── Plan-Quelle (aktueller Stand) ────────────────────────────────────────────
 * Solange kein echtes Billing-/Auth-System existiert, wird der aktuelle Plan
 * aus localStorage gelesen (PLAN_KEY). Default ist "business" für Entwicklung.
 *
 * Migration auf echte Plan-Daten:
 *   Heute:  localStorage.getItem(PLAN_KEY)
 *   Morgen: currentUser.subscription?.planId  (aus Auth-Context/API)
 *   → Nur useCurrentPlanId() anpassen — alle aufrufenden Stellen bleiben unverändert.
 *
 * ── Trial-Logik ───────────────────────────────────────────────────────────────
 * Während einer aktiven 14-Tage-Testphase gilt: allowed = true für alle Features.
 * Trial-Nutzer sollen den vollen Produktwert erleben — kein künstlicher Lock.
 *
 * ACHTUNG Kanal-Verbindungen sind KEIN Feature-Gate: Testphase = nur Bluesky,
 * Kauf = alle Kanäle. Das entscheidet der Server (GET /billing/plan →
 * channel_access; durchgesetzt in require_channel_connect_allowed) — nicht
 * dieser Hook. Siehe SocialConnectPage/channelButtonModel.
 *
 * isTrial  → true wenn Testphase aktiv (für kontextspezifische UI, z.B. Trial-Banner)
 * allowed  → true wenn Plan freigeschaltet ODER Trial aktiv
 *
 * Wenn Trial abläuft, fällt allowed automatisch auf den Plan-Wert zurück.
 * Kein zusätzlicher Aufruf nötig — ergibt sich aus useTrialStatus().isActive.
 *
 * ── Exports ──────────────────────────────────────────────────────────────────
 *   useCurrentPlanId()              → aktueller Plan-ID-String
 *   useFeatureGate(featureKey)      → {
 *     allowed, isTrial, currentPlan, currentPlanId, requiredPlan, upgradeHint,
 *     hasExplicitPlan, upgradeTarget, settingsHref, pricingHref
 *   }
 *
 * ── upgradeTarget-Logik ───────────────────────────────────────────────────────
 * "settings"  → Nutzer hat explizit einen Plan gewählt → Settings-Planverwaltung
 *               ist der produktnahe Upgrade-Touchpoint.
 * "pricing"   → kein expliziter Plan → PricingPage ist der richtige Einstieg.
 *
 * UpgradeNudge liest upgradeTarget und wählt den primären CTA entsprechend.
 */

import { useMemo } from "react";
import { pricingPlans, getRequiredPlanForFeature } from "../data/pricing";
import { useTrialStatus } from "./usePlanSelection";

// ── Plan-Schlüssel in localStorage ───────────────────────────────────────────
// Konsistent mit ROLE_KEY-Muster aus App.js.
// TODO: ersetzen durch echte Subscription-Quelle sobald Billing live ist.
const PLAN_KEY = "foerderfuchs_current_plan";

// Fallback wenn kein Plan gesetzt: Einstiegsplan zeigen → korrekte Upgrade-Nudges.
const DEFAULT_PLAN_ID = "solo";

// ── useCurrentPlanId ─────────────────────────────────────────────────────────

/**
 * Gibt die aktuelle Plan-ID zurück (aus localStorage oder Default).
 *
 * Defensive: gibt immer einen String zurück, niemals null/undefined.
 * Unbekannte gespeicherte Werte werden durch DEFAULT_PLAN_ID ersetzt.
 *
 * @returns {string} Plan-ID, z.B. "solo" | "business" | "creator" | "agency"
 */
export function useCurrentPlanId() {
  const stored = localStorage.getItem(PLAN_KEY);
  const isValid = pricingPlans.some((p) => p.id === stored);
  return isValid ? stored : DEFAULT_PLAN_ID;
}

// ── useFeatureGate ────────────────────────────────────────────────────────────

/**
 * Prüft, ob ein Feature für den aktuellen Plan freigeschaltet ist.
 *
 * @param {string} featureKey — z.B. "evergreen", "content_tools", "team_access"
 *
 * @returns {{
 *   allowed:          boolean,       — true wenn Feature für aktuellen Plan verfügbar
 *   currentPlan:      object|null,   — vollständiges Plan-Objekt des aktuellen Plans
 *   currentPlanId:    string,        — Plan-ID des aktuellen Plans
 *   requiredPlan:     object|null,   — erster Plan, der das Feature freischaltet
 *   upgradeHint:      string|null,   — Hinweis-Satz für das UI (null wenn allowed)
 *   hasExplicitPlan:  boolean,       — true wenn Nutzer explizit einen Plan gewählt hat
 *   upgradeTarget:    "settings"|"pricing", — bevorzugter Upgrade-Touchpoint
 *   settingsHref:     string,        — Link direkt zum Plan-Management-Block in Settings
 *   pricingHref:      string,        — Link zur PricingPage mit highlight
 * }}
 *
 * Defensive Fallbacks:
 *   - unbekannte featureKey → allowed: false, requiredPlan: null, kein Crash
 *   - unbekannte Plan-ID    → fällt auf solo zurück (striktester Plan)
 *   - featureGates fehlt    → allowed: false
 */
export function useFeatureGate(featureKey) {
  const currentPlanId = useCurrentPlanId();
  const trialStatus = useTrialStatus();

  return useMemo(() => {
    // Aktuellen Plan bestimmen — Fallback auf striktesten Plan (solo) bei Unbekanntem
    const currentPlan =
      pricingPlans.find((p) => p.id === currentPlanId) ?? pricingPlans[0] ?? null;

    // Feature-Gate auslesen — undefined/fehlendes Gate → nicht freigeschaltet
    const gates = currentPlan?.featureGates ?? {};
    const allowedByPlan = gates[featureKey] === true;

    // Trial-Override: während aktiver Testphase sind alle Features freigeschalten.
    // Nutzer sollen den vollen Produktwert erleben, um eine informierte Plan-Entscheidung
    // treffen zu können. Das isTrial-Flag erlaubt kontextspezifische UI-Behandlung
    // (z.B. Trial-Banner statt Upgrade-Nudge).
    const isTrial = trialStatus.isActive;
    const allowed = allowedByPlan || isTrial;

    // Ersten freischaltenden Plan finden (nur wenn nicht durch Plan erlaubt).
    // Bei Trial-Zugang: requiredPlan zeigt trotzdem welcher Plan es dauerhaft freischaltet
    // → wird für den "nach Trial"-Kontext benötigt (z.B. Trial-Banner CTA).
    const requiredPlan = allowedByPlan ? null : getRequiredPlanForFeature(featureKey);

    // Upgrade-Hinweis-Satz für das UI (null wenn plan-erlaubt oder trial)
    const upgradeHint = (!allowed && requiredPlan)
      ? `Diese Funktion ist ab ${requiredPlan.name} verfügbar.`
      : null;

    // Nutzer hat explizit einen Plan gewählt (nicht nur Dev-Fallback)?
    const hasExplicitPlan = localStorage.getItem(PLAN_KEY) !== null;

    // Bevorzugter Upgrade-Touchpoint:
    //   Bestehende Nutzer (expliziter Plan) → Settings-Planverwaltung
    //   Neue / unentschlossene Nutzer       → PricingPage
    const upgradeTarget = hasExplicitPlan ? "settings" : "pricing";

    // Fertige Hrefs — UpgradeNudge kann direkt verwenden
    const settingsHref = "/settings#plan-management";
    const pricingHref = requiredPlan
      ? `/pricing?highlight=${requiredPlan.id}`
      : "/pricing";

    return {
      allowed,
      isTrial,
      currentPlan,
      currentPlanId: currentPlan?.id ?? DEFAULT_PLAN_ID,
      requiredPlan,
      upgradeHint,
      hint: upgradeHint, // Alias für direktes Spread in UpgradeNudge ({...gate})
      hasExplicitPlan,
      upgradeTarget,
      settingsHref,
      pricingHref,
    };
  }, [featureKey, currentPlanId, trialStatus.isActive]);
}
